import { useEffect } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { breakpoints } from "common/styles";
import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { covers } from "domains/resources/covers";
import getStarted from "domains/resources/data/getStarted.json";
import introduction from "domains/resources/data/introduction.json";
import { VideoCover } from "domains/resources/VideoCover";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";

const StyledIntroductionGrid = styled.div`
  display: grid;
  grid-gap: 3rem 2rem;
  grid-template-columns: 100%;
  margin-bottom: 2rem;

  @media ${breakpoints.large} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StyledIntroduction = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledIntroductionLabel = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
`;

const StyledIntroductionTitle = styled.div`
  color: ${({ theme }) => theme.colors.textBright};
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0.6rem 0 0.8rem;
  text-transform: capitalize;
`;

const StyledIntroductionDescription = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
`;

const { image, length, name, url } = introduction;

export const Introduction = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { pathname, search } = useLocation();
  const track = useTrack();
  const translate = useTranslate();

  const videoUrlParam = new URLSearchParams(search).get("video");

  useEffect(() => {
    if (videoUrlParam !== name) return;
    dispatch(
      setVisualMediaPlayer({
        id: "introduction",
        mediaType: MediaType.videoViaEmbeddedHtmlPlayer,
        name,
        onCloseRoute: pathname,
        splash: { next: getStarted[0] },
        url,
        isYoutubeVideo: true,
      }),
    );
  }, [dispatch, go, pathname, translate, videoUrlParam]);

  const onClick = () => {
    track({ name: "Play Media", page: "Resources", entityName: name, entityId: url });
    go(`${pathname}?video=${name}`);
  };

  return (
    <StyledIntroductionGrid>
      <VideoCover
        height="350px"
        image={covers[image as keyof typeof covers]}
        onClick={onClick}
        videoLength={length}
        videoName="introduction"
      />
      <StyledIntroduction>
        <StyledIntroductionLabel>{translate("app.resources.label.video")}</StyledIntroductionLabel>
        <StyledIntroductionTitle>Strategic Intelligence - Introduction</StyledIntroductionTitle>
        <StyledIntroductionDescription>
          Strategic Intelligence offers a hybrid model combining artificial intelligence and human expertise to help
          users anticipate and understand global transformations. Dive into this conversation with Cosima Piepenbrock,
          Head of Product at the World Economic Forum, discussing Strategic Intelligence as a tool for decision-makers
          in today&apos;s complex world.
        </StyledIntroductionDescription>
      </StyledIntroduction>
    </StyledIntroductionGrid>
  );
};
